// The electrical schematic's MODEL (docs/claude/elecSchematic.md): what it
// shows, derived live from the project each time, with the hand edits stored on
// `Drawing.elecSchematic` applied on top. Pure (no DOM): the layout
// (elecLayout.ts) places it on paper, the sheet draws and picks that.
//
// Columns are the rooms with something electrical; rows are fixed; a box is a
// device (a product's part, a room's light group, a client provision, one added
// by hand) in one cell; a wire is a project connection, an automatic feed (a
// part's or a light's, to the nearest junction box at or below it), a
// preparation's client supply, or one drawn by hand.

import type {
  Connection,
  ConnectionType,
  Drawing,
  ElecBorderEdit,
  ElecBoxAngle,
  ElecCellId,
  ElecPlantRoom,
  ElecRowId,
  ElecSchematic,
  ElecWireEdit,
  Lang,
  ProductInstance,
  ProductVariant,
  RoomLight,
  SchematicLineStyle,
  TextI18n,
  Vec2,
} from '../shared/types';
import { CONNECTION_TYPES } from '../shared/types';
import {
  connectionSchedule,
  preparationSchedule,
  scheduleNumbers,
} from './schedule';
import {
  coveGeometry,
  lightProduct,
  lightProductName,
  lightTagKey,
  lightWatts,
  pathLengthOf,
  stripRuns,
} from './lights';
import {
  externalHeight,
  roomBase,
} from './heights';
import { pointAtFraction } from './geometry';
import { isComputedLength } from './cableLength';
import { isFibreKit } from './lightLife';
import {
  connDiscipline,
  objectMidHeight,
} from './connections';
import { numberedNames } from './naming';
import { placedVariant } from './variants';
import { productName } from '../shared/names';
import { traitsOf } from '../shared/categories';

/** The rows, top to bottom. */
export const ELEC_ROWS: readonly ElecRowId[] = ['outside', 'cabin', 'ceiling', 'technical', 'client'];
/** The rows that follow the columns. */
export const ELEC_COLUMN_ROWS: readonly ElecRowId[] = ['outside', 'cabin', 'ceiling'];
/** Wire colours by who lays it (decision 17). */
export const ELEC_STENAL = '#4a90d9';
export const ELEC_CLIENT = '#e07020';
/** Colours a wire is drawn in, by who lays it. */
export const ELEC_INSTALLER_COLORS = { stenal: ELEC_STENAL, client: ELEC_CLIENT } as const;
/** A wire's line width (paper mm) where neither it nor the schematic sets one. */
export const ELEC_WIRE_THICKNESS = 0.3;
/** A jog dragged by hand: the fraction of the way down, kept off its two ends. */
export const clampElecJog = (t: number): number => Math.max(0.02, Math.min(0.98, t));
/** Columns' default colours, by column, round and round. */
export const ELEC_COLUMN_COLORS: readonly string[] = ['#f7efe0', '#f5e6f7', '#e3f7ec', '#e6f4fa', '#fdf3d0', '#ecebf7'];
/** The default plant room's id. */
export const ELEC_MAIN_PLANT = 'main';

/** The keys an edit names its target by (docs/claude/elecSchematic.md, *Keys*):
 *  built here only, so their format is known in one place. A connection's first
 *  line keeps the connection's key however many lines it has. */
export const ELEC_KEY = {
  part: (inst: string): string => `p:${inst}`,
  light: (room: string, light: string): string => `l:${room}:${light}`,
  client: (inst: string, prep: string): string => `c:${inst}:${prep}`,
  hand: (id: string): string => `h:${id}`,
  conn: (c: string, line = 1): string => (line > 1 ? `e:${c}:${line}` : `e:${c}`),
  /** Line k ≥ 2 of a wire of several parallel cables that is no connection's
   *  (a hand wire, an automatic feed, a preparation's): `<its key>#<k>`. */
  line: (key: string, k: number): string => (k > 1 ? `${key}#${k}` : key),
  /** A box's automatic feed (decision 26): a light group's or a part's. */
  lightWire: (box: string): string => `la:${box}`,
  /** The light group a box key or a light's wire key names, else null. */
  lightOf: (key: string): string | null => {
    const k = key.replace(/#\d+$/, '');
    return k.startsWith('l:') ? k : k.startsWith('la:l:') ? k.slice(3) : null;
  },
  prep: (inst: string, prep: string): string => `pr:${inst}:${prep}`,
  handWire: (id: string): string => `s:${id}`,
} as const;

/** Translates an English source string (the DICT key) into the drawing's language. */
export type ElecTr = (en: string) => string;

export interface ElecOptions {
  /** The fixed strings into the drawing's language (i18n.ts `trIn`); absent = English. */
  tr?: ElecTr;
  /** A connection's worked-out cable length (mm), for one with none typed:
   *  cableLength.ts `connectionLength(drawing, c)`. Absent = only typed lengths. */
  lengthOf?: (c: Connection) => number | undefined;
}

export type ElecBoxKind = 'client' | 'hand' | 'light' | 'product';
export type ElecWireKind = 'auto' | 'hand' | 'prep' | 'project';

export interface ElecColumn {
  id: string;
  /** The room it is (absent: added by hand). */
  roomId?: string;
  /** As shown: the name entered for it, else its default. */
  name: string;
  /** The name entered for it, as typed (absent: none). */
  entered?: string;
  color: string;
  hand: boolean;
}

export interface ElecRow {
  id: ElecRowId;
  name: string;
  empty: boolean;
  /** Hidden by hand (only while empty). */
  hidden: boolean;
}

/** A load a box draws, for "List the loads it feeds". */
export interface ElecLoad { name: string; power: number; voltage?: string }

export interface ElecBox {
  /** Stable: `p:<instance>`, `l:<room>:<light group>`, `c:<instance>:<preparation>`, `h:<hand box>`. */
  key: string;
  kind: ElecBoxKind;
  /** A part whose product is a junction box (`isJunctionBox`): drawn as a bar,
   *  the boxes at or above it wired to it. */
  junction?: boolean;
  /** The Spa Manager (drawn in its own colour). */
  manager?: boolean;
  cell: ElecCellId;
  /** The cell the rules give it (before a hand move). */
  autoCell: ElecCellId;
  row: ElecRowId;
  /** For the slots' order: the product's category ('lighting' for lights). */
  category: string;
  /** For the slots' order, and the loads' lines. */
  name: string;
  /** Its text, a line each; the hand text when replaced. */
  lines: string[];
  /** Its own text, a line each — the hand text, else the automatic one —
   *  without the lines the schematic works out on top (the loads it lists, its
   *  Total): what a text field starts from and is compared with. */
  ownLines: string[];
  edited: boolean;
  /** Its hand order in its cell (decision 15); absent = the automatic order after. */
  order?: number;
  /** Its edits are stored under this older key of it (elecEdits.ts moves them). */
  editKey?: string;
  /** Not where the rules put it: in another cell, or out of the automatic order
   *  among its cell's boxes that are (`moved` is the fewest that are not). */
  moved: boolean;
  /** Turned by hand (`ElecBoxEdit.rotation`); absent = its line's own. */
  rotation?: ElecBoxAngle;
  /** A light's or audio's power, grouped under "Various loads". */
  various?: boolean;
  /** Its own loads: the variant's power, then each extra load. */
  loads: ElecLoad[];
  /** "List the loads it feeds" is on: what it feeds, and the total (W). */
  feeds?: { loads: ElecLoad[]; various: number; total: number };
  /** "Electrical load" is on: the power it needs (W), its own and what it feeds. */
  load?: number;
  /** Where it comes from. */
  instId?: string;
  roomId?: string;
  /** A light group's: the lights an older key of it names it by (none for a
   *  rounded corner's LED); `run`, a segment of a run of several or that run
   *  as one box. */
  lightIds?: string[];
  run?: ElecLightRun;
  /** A segment's place along its run (absent = 0): a run's LEDs stand
   *  together in their cell, in path order (`compareElecBoxesAuto`). */
  runAt?: number;
  /** A fibre kit's box: its projector parts (ids), what a connection's end at
   *  it is glued to (`elecBoxPart`). */
  projectors?: string[];
  prepId?: string;
  handId?: string;
  /** Put in its cell's hand order by hand last (`ElecBoxEdit.dragged`): the one
   *  `moved` of two swapped. */
  dragged?: boolean;
}

export interface ElecWire {
  /** Stable: `e:<connection>[:<line>]`, `la:<its box key>` (automatic), `pr:<instance>:<preparation>`, `s:<hand wire>`. */
  key: string;
  kind: ElecWireKind;
  from: string;
  to: string;
  /** E… (the project's number), S… (hand); `.k` for line k of several. */
  number: string;
  /** Of a wire of several parallel cables running the same path, which one
   *  (1 …) and how many (`lines`, those removed too: a connection's `lines`, a
   *  hand wire's, an automatic or a preparation's edit's); absent = one. */
  line?: number;
  lines?: number;
  /** Line 2 on: its first line's key, where the wire's figures and its count
   *  are kept (its own: its look, its jog, removed or not). */
  first?: string;
  installedBy: 'stenal' | 'client';
  /** mm: the length typed by hand, else the worked-out one; absent = unknown. */
  length?: number;
  /** The length typed by hand (where it is kept: the connection, the hand wire,
   *  the wire's edit). */
  lengthTyped?: number;
  /** The length worked out from the route, where it can be (a project wire, a light's). */
  lengthAuto?: number;
  /** The cable (a connection's description). */
  cable: string;
  connectionId?: string;
  /** A hand wire's id. */
  handId?: string;
  /** Its edits are stored under this older key of it (elecEdits.ts moves them). */
  editKey?: string;
  /** An automatic or a preparation's wire's far end, taken to this box by hand. */
  fed?: string;
  jog?: number;
  straight?: boolean;
  /** Drawn by hand (`ElecWireEdit`): line width (paper mm), dash, colour. */
  thickness?: number;
  style?: SchematicLineStyle;
  color?: string;
  /** How it is drawn: its own, else the schematic's width and dash, else its
   *  installer's colour. */
  look: { thickness: number; style: SchematicLineStyle; color: string };
}

export interface ElecModel {
  lang: Lang;
  rows: ElecRow[];
  /** The visible columns, in order. */
  columns: ElecColumn[];
  /** Columns hidden by hand (their boxes and wires are left out). */
  hiddenColumns: ElecColumn[];
  plantRooms: ElecModelPlantRoom[];
  /** In each cell's slot order. */
  boxes: ElecBox[];
  wires: ElecWire[];
  boxByKey: Map<string, ElecBox>;
  /** What feeds each box (decision 26), by box key: the boxes below it a wire
   *  drawn joins it to, its automatic feed's (a removed one too), and for a
   *  junction box fed through its row's group, the one feeding it
   *  (`elecFeedLoops`). */
  fedBy: Map<string, string[]>;
  /** Hidden by hand (each to show again), and the wires removed by hand. */
  hiddenBoxes: ElecBox[];
  removedWires: ElecWire[];
  /** The hand edits the layout reads as they are. */
  rowHeights: Partial<Record<ElecRowId, number>>;
  borders: ElecBorderEdit[];
  /** Every wire's line width and dash, where its own sets none. */
  wireThickness: number;
  wireStyle: SchematicLineStyle;
}

// ---- Cells -------------------------------------------------------------------------

export function elecCellId(row: ElecRowId, key = ''): ElecCellId {
  return (row === 'client' ? 'client' : `${row}:${key}`) as ElecCellId;
}
/** A cell's row and its column / plant room id ('' for the client cell). */
export function parseElecCell(cell: ElecCellId): { row: ElecRowId; key: string } {
  if (cell === 'client') return { row: 'client', key: '' };
  const i = cell.indexOf(':');
  return { row: cell.slice(0, i) as ElecRowId, key: cell.slice(i + 1) };
}

/** A row's name, translated. */
export function elecRowName(row: ElecRowId, tr: ElecTr): string {
  switch (row) {
    case 'outside': return tr('Outside');
    case 'cabin': return tr('Inside cabin');
    case 'ceiling': return tr('Ceiling above cabin');
    case 'technical': return tr('Technical rooms');
    case 'client': return tr('Client provisions');
  }
}

/** A plant room as the model shows it: the name entered for it, else its default. */
export interface ElecModelPlantRoom extends ElecPlantRoom {
  name: string;
  entered?: string;
}

/** The plant rooms: the stored list, else the one main technical room. */
export function elecPlantRooms(sch: ElecSchematic | undefined): ElecPlantRoom[] {
  return sch?.plantRooms?.length ? sch.plantRooms : [{ id: ELEC_MAIN_PLANT }];
}

/** The word a plant room is called by while no name is entered for it (a
 *  dictionary key): the main technical room, else a technical room. */
export function elecPlantRoomWord(p: Pick<ElecPlantRoom, 'id'>): string {
  return p.id === ELEC_MAIN_PLANT ? 'Main technical room' : 'Technical room';
}

/** The words the schematic calls a hand column and a hand box by while nothing
 *  is typed for them (dictionary keys). */
export const ELEC_NEW_COLUMN = 'New column';
export const ELEC_NEW_BOX = 'New box';

/** A schematic saved when its typed texts were kept in every language (the
 *  others machine-translated), or when the names it gives were stored as text:
 *  each text becomes the one the drawing showed in its own language, entered as
 *  typed; one that is a name the schematic gives, in any language, becomes that
 *  default again (docs/claude/names.md). Once, when a drawing is opened. */
export function settleElecTexts(sch: ElecSchematic | undefined, lang: Lang, trIn: (key: string, lang: Lang) => string): void {
  if (!sch) return;
  const settle = (o: object, field: string, word?: string): void => {
    const bag = o as Record<string, unknown>, mapKey = `${field}I18n`;
    const map = bag[mapKey] as TextI18n | undefined;
    delete bag[mapKey];
    const own = typeof bag[field] === 'string' ? (bag[field] as string) : undefined;
    const shown = map?.[lang]?.trim() ? map[lang]! : own;
    if (shown == null) return;
    if (word && (['en', 'it', 'fr'] as const).some((l) => trIn(word, l) === shown.trim())) {
      if (field === 'text') bag[field] = ''; else delete bag[field];
      return;
    }
    bag[field] = shown;
  };
  for (const p of sch.plantRooms ?? []) settle(p, 'name', elecPlantRoomWord(p));
  for (const c of sch.columns ?? []) settle(c, 'name', c.hand ? ELEC_NEW_COLUMN : undefined);
  for (const b of Object.values(sch.boxes ?? {})) settle(b, 'text');
  for (const h of sch.handBoxes ?? []) settle(h, 'text', ELEC_NEW_BOX);
  for (const w of Object.values(sch.wires ?? {})) settle(w, 'cable');
  for (const h of sch.handWires ?? []) settle(h, 'cable');
}

// ---- Numbers and text ---------------------------------------------------------------

/** A figure with at most `dec` decimals, trailing zeros dropped, in the language's
 *  decimal separator. */
export function elecNum(v: number, lang: Lang, dec = 2): string {
  const f = 10 ** dec;
  const s = String(Math.round(v * f) / f);
  return lang === 'en' ? s : s.replace('.', ',');
}
/** Watts as W, or kW from 1000 W. */
export function elecPower(w: number, lang: Lang): string {
  return Math.abs(w) >= 1000 ? `${elecNum(w / 1000, lang)} kW` : `${elecNum(w, lang)} W`;
}
const volts = (v: string): string => (/^\s*[\d.,]+\s*$/.test(v) ? `${v.trim()} V` : v.trim());

/** A variant's rating on one line: power / voltage / current. */
export function variantLine(v: ProductVariant, lang: Lang): string {
  const parts: string[] = [];
  if (v.power != null) parts.push(elecPower(v.power, lang));
  if (v.voltage?.trim()) parts.push(volts(v.voltage));
  if (v.current != null) parts.push(`${elecNum(v.current, lang)} A`);
  return parts.join(' / ');
}
const loadLine = (l: ElecLoad, lang: Lang): string => `${l.name} ${elecPower(l.power, lang)}${l.voltage?.trim() ? ` / ${volts(l.voltage)}` : ''}`;

const i18n = (base: string, m: TextI18n | undefined, lang: Lang): string => m?.[lang]?.trim() || base;

// ---- Which parts are what -----------------------------------------------------------

/** A part whose product is a junction box (its Junction box field): the Spa
 *  Manager, a junction box. */
export function isJunctionBox(inst: Pick<ProductInstance, 'junctionBox'>): boolean {
  return !!inst.junctionBox;
}

/** A room: a column of the schematic, never a device (decision 5). It takes no
 *  electrical preparation: the Preparations tab offers none
 *  (`preparationTypes`), and one stored on it anyway gets no client box. */
export function isElecRoom(inst: Pick<ProductInstance, 'autoBenches'>): boolean {
  return !!inst.autoBenches;
}
const isRoom = isElecRoom;

/** The disciplines a preparation on `room` (a room, else a part or a polygon)
 *  may take: every one, but on a room no electrical one — unless it is the
 *  `current` one's, stored before. */
export function preparationTypes(room: boolean, current?: ConnectionType): ConnectionType[] {
  return CONNECTION_TYPES.filter((t) => !room || t !== 'electrical' || t === current);
}
const isElecConn = (c: Connection): boolean => connDiscipline(c) === 'electrical';

/** Decision 5: a part is electrical when its product's Electrical or Junction
 *  box field is on, or it has an electrical preparation, an electrical
 *  connection point (an untyped one does not count) or an electrical connection
 *  drawn to it. A room never is a device: it is a column. */
export function isElectricalPart(inst: ProductInstance, drawing: Pick<Drawing, 'connections'>): boolean {
  if (isRoom(inst)) return false;
  if (inst.electrical || isJunctionBox(inst)) return true;
  if (inst.preparations?.some((p) => p.type === 'electrical')) return true;
  if (inst.ports?.some((p) => p.type === 'electrical')) return true;
  return drawing.connections.some((c) => isElecConn(c) && (c.from === inst.id || c.to === inst.id));
}

function toLocal(room: ProductInstance, p: Vec2): Vec2 {
  const a = (-room.rotation * Math.PI) / 180, dx = p.x - room.x, dy = p.y - room.y;
  return { x: dx * Math.cos(a) - dy * Math.sin(a), y: dx * Math.sin(a) + dy * Math.cos(a) };
}
function toWorld(room: ProductInstance, p: Vec2): Vec2 {
  const a = (room.rotation * Math.PI) / 180;
  return { x: room.x + p.x * Math.cos(a) - p.y * Math.sin(a), y: room.y + p.x * Math.sin(a) + p.y * Math.cos(a) };
}
function inRing(p: Vec2, pts: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}
/** The room whose outline holds a plan point (the topmost), as `saunaAt`. */
export function elecRoomAt(drawing: Pick<Drawing, 'instances'>, p: Vec2): ProductInstance | null {
  for (let i = drawing.instances.length - 1; i >= 0; i--) {
    const r = drawing.instances[i];
    if (!isRoom(r)) continue;
    const q = toLocal(r, p);
    if (r.polygon && r.polygon.length >= 3 ? inRing(q, r.polygon) : Math.abs(q.x) <= r.width / 2 && Math.abs(q.y) <= r.depth / 2) return r;
  }
  return null;
}

/** Decision 6: inside a room's outline its column, Inside cabin up to the room's
 *  height and Ceiling above cabin above it; outside every room the first plant room. */
export function elecAutoCell(drawing: Pick<Drawing, 'instances'>, inst: ProductInstance, mainPlant: string): ElecCellId {
  const room = elecRoomAt(drawing, { x: inst.x, y: inst.y });
  if (!room) return elecCellId('technical', mainPlant);
  const top = roomBase(room) + externalHeight(room);
  return elecCellId((inst.heightFromGround ?? 0) >= top - 1 ? 'ceiling' : 'cabin', room.id);
}

/** The variant a part shows: its own, else — for the first electrical part of a
 *  placed multi-part product — its group's. A multi-part product unlinked from
 *  its catalogue product (a per-part edit, `unlinkProductGroup`) carries a copy
 *  of its variant on every part: the product's load still, counted once — on its
 *  first electrical part, each other part showing only a variant of its own. */
function variantsOf(drawing: Drawing, parts: ProductInstance[]): Map<string, ProductVariant> {
  const out = new Map<string, ProductVariant>();
  const groups = new Map((drawing.groups ?? []).map((g) => [g.id, g]));
  const taken = new Set<string>();
  const shown = new Map<string, Set<string>>();
  for (const inst of parts) {
    if (inst.variant) {
      if (inst.partId != null && inst.groupId) {
        const ids = shown.get(inst.groupId) ?? new Set<string>();
        shown.set(inst.groupId, ids);
        if (ids.has(inst.variant.id)) continue;
        ids.add(inst.variant.id);
      }
      out.set(inst.id, inst.variant);
      continue;
    }
    for (let g = inst.groupId ? groups.get(inst.groupId) : undefined, n = 0; g && n < 64; g = g.parent ? groups.get(g.parent) : undefined, n++) {
      if (!g.source) continue;
      if (g.source.variant && !taken.has(g.id)) { out.set(inst.id, g.source.variant); taken.add(g.id); }
      break;
    }
  }
  return out;
}

/** Audio, grouped with the lights under "Various loads". */
const isAudio = (category: string): boolean => /audio|speaker|sound/i.test(category);

// ---- Slot order (decision 15) --------------------------------------------------------

/** The schematic's order of categories in a cell: each category's `elecRank`,
 *  any other after them (by name), and the hand-drawn boxes last. */
const UNRANKED = 1e9;
function categoryRank(b: ElecBox): number {
  if (b.kind === 'hand') return UNRANKED + 1;
  return traitsOf(b.category).elecRank ?? UNRANKED;
}
/** The automatic order: by category, then name. */
export function compareElecBoxesAuto(a: ElecBox, b: ElecBox): number {
  return categoryRank(a) - categoryRank(b) || a.category.localeCompare(b.category) || a.name.localeCompare(b.name)
    || runHead(a).localeCompare(runHead(b)) || (a.runAt ?? 0) - (b.runAt ?? 0) || a.key.localeCompare(b.key);
}
/** A run's LEDs together, in path order: by its first light's key, as the run's own box. */
function runHead(b: ElecBox): string {
  return b.run && b.roomId ? ELEC_KEY.light(b.roomId, b.run.ids[0]) : b.key;
}
/** Hand-ordered first (by their order), then the automatic order. */
export function compareElecBoxes(a: ElecBox, b: ElecBox): number {
  if (a.order != null || b.order != null) {
    if (a.order == null) return 1;
    if (b.order == null) return -1;
    if (a.order !== b.order) return a.order - b.order;
  }
  return compareElecBoxesAuto(a, b);
}

/** Which of `list` (as they stand) are out of the order `rank` gives them: the
 *  FEWEST — the others are the longest run in that order. Of two such runs, one
 *  without a box `dragged` there by hand: of two boxes swapped, the one dragged
 *  is out of place, not the one it was dragged past. */
export function elecOutOfOrder<T>(list: readonly T[], rank: (t: T) => number, dragged: (t: T) => boolean): Set<T> {
  const r = list.map(rank);
  const run = (use: (i: number) => boolean): Set<number> => {
    const len = r.map(() => 0), prev = r.map(() => -1);
    for (let i = 0; i < r.length; i++) {
      if (!use(i)) continue;
      len[i] = 1;
      for (let j = 0; j < i; j++) if (len[j] && r[j] < r[i] && len[j] + 1 > len[i]) { len[i] = len[j] + 1; prev[i] = j; }
    }
    const kept = new Set<number>();
    let at = len.reduce((best, l, i) => (l > len[best] ? i : best), 0);
    for (; at >= 0 && len[at]; at = prev[at]) kept.add(at);
    return kept;
  };
  const all = run(() => true);
  const without = list.some(dragged) ? run((i) => !dragged(list[i])) : all;
  const kept = without.size === all.size ? without : all;
  return new Set(list.filter((_, i) => !kept.has(i)));
}

/** Which boxes of a cell (in its order) are moved: those from another cell, and
 *  the fewest out of the automatic order among the rest (`elecOutOfOrder`; of
 *  two swapped, the one dragged). */
function markMoved(list: ElecBox[]): void {
  const home = list.filter((b) => b.cell === b.autoCell);
  for (const b of list) b.moved = b.cell !== b.autoCell;
  const rank = new Map([...home].sort(compareElecBoxesAuto).map((b, i) => [b, i]));
  const out = elecOutOfOrder(home, (b) => rank.get(b)!, (b) => !!b.dragged);
  for (const b of home) b.moved = out.has(b);
}

// ---- The model ----------------------------------------------------------------------

/** A room's light group: `at` room local; a fibre kit's `projectors` (its
 *  projector parts' ids) and their middle on the plan (`world`). */
interface LightGroup {
  key: string; room: ProductInstance; lights: RoomLight[]; name: string; lines: string[]; watts: number; at: Vec2; cell: ElecCellId; world?: Vec2; projectors: string[];
  /** The lights an older key of it may name it by (`namesGroup`, `lightKeys`):
   *  its lights', none for a rounded corner's LED (no light of its own). */
  ids: string[];
  /** A segment of a run of several, or the run as one box (`whole`); a
   *  segment's place along it (0 …). */
  run?: ElecLightRun;
  runAt?: number;
}

/** A linear light's run of several LEDs (`ElecBox.run`): the lights it is made
 *  of, and whether it is one box (`ElecSchematic.wholeLights`) or one per LED. */
export interface ElecLightRun { ids: string[]; whole: boolean }

/** A room's light groups (decision 12): a strip, a cove or a channel its
 *  length × W/m = W; spots of one product count × W; a starry sky its kit. A
 *  strip run is one group per LED — each bench's or backrest's strip, each
 *  rounded corner between two — and a cove one per wall and rounded corner
 *  (`StripRun.segments`, `CoveSegment`), each its own box and wire, unless its
 *  run is one box (`whole`: a light id of it in `wholeLights`); a channel is
 *  one however it turns. A fibre kit (a starry sky, a 1-fibre point) is its
 *  projector: its box stands where the projector part is (`cellOf`, as any
 *  part — over the ceiling, out of the room), the room's Inside cabin while it
 *  has none. */
function lightGroups(room: ProductInstance, lang: Lang, tr: ElecTr, partOf: (id: string) => ProductInstance | undefined, cellOf: (inst: ProductInstance) => ElecCellId, whole: ReadonlySet<string>): LightGroup[] {
  const lights = room.lights ?? [];
  if (!lights.length) return [];
  const out: LightGroup[] = [];
  const cabin = elecCellId('cabin', room.id);
  const nameOf = (l: RoomLight): string => lightProductName(l, lightProduct(l), lang) || tr('Light');
  const metres = (ls: RoomLight[], mm: number, at: Vec2, id: string, ids: string[], run?: ElecLightRun, runAt?: number): void => {
    const l = ls[0], w = lightWatts(l, mm), wpm = lightProduct(l)?.powerPerMeter ?? 0;
    out.push({ key: ELEC_KEY.light(room.id, id), room, lights: ls, name: nameOf(l), lines: [nameOf(l), `${elecNum(mm / 1000, lang)} m × ${elecNum(wpm, lang)} W/m = ${elecNum(w, lang)} W`], watts: w, at, cell: cabin, projectors: [], ids, ...(run ? { run } : {}), ...(runAt ? { runAt } : {}) });
  };
  const projectorOf = (l: RoomLight): ProductInstance | undefined => (isFibreKit(l) && l.projectorId ? partOf(l.projectorId) : undefined);
  const byId = new Map(lights.map((l) => [l.id, l]));
  const lightsOf = (ids: string[]): RoomLight[] => ids.map((id) => byId.get(id)).filter((x): x is RoomLight => !!x);
  for (const run of stripRuns(room)) {
    const ls = lightsOf(run.ids);
    if (!ls.length) continue;
    const segs = run.segments.filter((g) => lightsOf(g.ids).length);
    const joined = segs.length < 2 || run.ids.some((id) => whole.has(id));
    const many: ElecLightRun | undefined = segs.length > 1 ? { ids: [...run.ids], whole: joined } : undefined;
    if (joined) { metres(ls, run.length, pointAtFraction(run.pts, 0.5), run.ids[0], run.ids, many); continue; }
    // Its LEDs: a strip's by its id (`~2` on for another piece of it a cut
    // left), a rounded corner's by the two it joins.
    const seen = new Map<string, number>();
    segs.forEach((g, i) => {
      const base = g.corner ? [...g.ids].sort().join('+') : g.ids[0];
      const n = (seen.get(base) ?? 0) + 1;
      seen.set(base, n);
      metres(lightsOf(g.ids), g.length, pointAtFraction(g.pts, 0.5), n > 1 ? `${base}~${n}` : base, g.corner ? [] : g.ids, many, i);
    });
  }
  const pieces = new Map<string, { lights: RoomLight[]; at: Vec2[]; cell: ElecCellId; world: Vec2[]; projectors: string[] }>();
  for (const l of lights) {
    if (l.kind === 'cove') {
      const runs = coveGeometry(room)?.runs ?? [], segs = runs.flatMap((r) => r.segments);
      const many: ElecLightRun | undefined = segs.length > 1 ? { ids: [l.id], whole: whole.has(l.id) } : undefined;
      if (!many || many.whole) metres([l], runs.reduce((m, r) => m + r.length, 0), { x: 0, y: 0 }, l.id, [l.id], many);
      else segs.forEach((g, i) => metres([l], g.length, pointAtFraction(g.pts, 0.5), `${l.id}~${g.corner != null ? `c${g.corner}` : `w${g.wall}`}`, [l.id], many, i));
    } else if (l.kind === 'channel') metres([l], pathLengthOf(l.pts), pointAtFraction(l.pts, 0.5), l.id, [l.id]);
    else if (l.kind === 'spot' || l.kind === 'sky') {
      const proj = projectorOf(l), cell = proj ? cellOf(proj) : cabin;
      const k = `${l.kind === 'sky' ? `sky:${l.id}` : lightTagKey(l)}@${cell}`;
      const g = pieces.get(k) ?? { lights: [], at: [], cell, world: [], projectors: [] };
      g.lights.push(l);
      g.at.push(l.kind === 'spot' ? l.at : { x: 0, y: 0 });
      if (proj) { g.world.push({ x: proj.x, y: proj.y }); g.projectors.push(proj.id); }
      pieces.set(k, g);
    }
  }
  const mean = (ps: Vec2[]): Vec2 => ({ x: ps.reduce((s, p) => s + p.x, 0) / ps.length, y: ps.reduce((s, p) => s + p.y, 0) / ps.length });
  for (const g of pieces.values()) {
    const l = g.lights[0], n = g.lights.length, each = lightWatts(l, 1), w = lightWatts(l, n);
    out.push({ key: ELEC_KEY.light(room.id, l.id), room, lights: g.lights, name: nameOf(l), lines: [nameOf(l), `${n} × ${elecNum(each, lang)} W = ${elecNum(w, lang)} W`], watts: w, at: mean(g.at), cell: g.cell, ...(g.world.length ? { world: mean(g.world) } : {}), projectors: g.projectors, ids: g.lights.map((x) => x.id) });
  }
  return out;
}

const NUM = /^([A-Z]+)(\d+)(?:\.(\d+))?$/;
/** Wire numbers in table order: E before S, then numerically. */
export function compareElecNumbers(a: string, b: string): number {
  const ma = NUM.exec(a), mb = NUM.exec(b);
  if (!ma || !mb) return a.localeCompare(b);
  return ma[1].localeCompare(mb[1]) || Number(ma[2]) - Number(mb[2]) || Number(ma[3] ?? 0) - Number(mb[3] ?? 0);
}

/** The schematic of a drawing, its hand edits applied. */
export function buildElecSchematic(drawing: Drawing, opts: ElecOptions = {}): ElecModel {
  const tr: ElecTr = opts.tr ?? ((s) => s);
  const lang: Lang = drawing.language ?? 'it';
  const sch: ElecSchematic = drawing.elecSchematic ?? {};
  const plantRooms: ElecModelPlantRoom[] = elecPlantRooms(sch).map((p) => {
    const entered = p.name?.trim() ? p.name : undefined;
    return { ...p, name: entered ?? tr(elecPlantRoomWord(p)), ...(entered ? { entered } : {}) };
  });
  const plantIds = new Set(plantRooms.map((p) => p.id));
  const rooms = drawing.instances.filter(isRoom);
  const handCols = (sch.columns ?? []).filter((c) => c.hand);
  const colIds = new Set([...rooms.map((r) => r.id), ...handCols.map((c) => c.id)]);
  const cellExists = (cell: ElecCellId): boolean => {
    const { row, key } = parseElecCell(cell);
    if (row === 'client') return true;
    if (row === 'technical') return plantIds.has(key);
    return ELEC_COLUMN_ROWS.includes(row) && colIds.has(key);
  };

  // ---- Boxes, before the hand edits.
  const boxes: ElecBox[] = [];
  const mainPlant = plantRooms[0].id;
  const partById = new Map(drawing.instances.map((i) => [i.id, i]));
  const wholeLights = new Set(sch.wholeLights ?? []);
  const groups: LightGroup[] = rooms.flatMap((r) => lightGroups(r, lang, tr, (id) => partById.get(id), (inst) => elecAutoCell(drawing, inst, mainPlant), wholeLights));
  // A fibre kit's projector part is the kit's box, never one of its own (decision
  // 26): what names the part's box names the kit's.
  const kitOf = new Map<string, string>();
  for (const g of groups) for (const id of g.projectors) if (!kitOf.has(id)) kitOf.set(id, g.key);
  const partKey = (id: string): string => kitOf.get(id) ?? ELEC_KEY.part(id);
  const kitByPartKey = new Map([...kitOf].map(([id, kit]) => [ELEC_KEY.part(id), kit]));
  const parts = drawing.instances.filter((i) => !kitOf.has(i.id) && isElectricalPart(i, drawing));
  const variants = variantsOf(drawing, parts);
  const partAt = new Map<string, Vec2>();
  // A part reads as on the plan, the Parts tab and the schedules (naming.ts).
  const names = numberedNames(drawing.instances, (i) => productName(i, lang));
  for (const inst of parts) {
    const junction = isJunctionBox(inst);
    const name = names.get(inst.id) ?? productName(inst, lang);
    const v = variants.get(inst.id);
    const loads: ElecLoad[] = [];
    if (v?.power != null) loads.push({ name, power: v.power, voltage: v.voltage });
    for (const l of v?.loads ?? []) loads.push({ name: l.name, power: l.power, voltage: l.voltage });
    const lines = [name];
    if (v && variantLine(v, lang)) lines.push(variantLine(v, lang));
    for (const l of v?.loads ?? []) lines.push(loadLine(l, lang));
    const cell = elecAutoCell(drawing, inst, mainPlant);
    boxes.push({ key: ELEC_KEY.part(inst.id), kind: 'product', ...(junction ? { junction } : {}), ...(inst.templateId === 'builtin-spamanager' ? { manager: true } : {}), cell, autoCell: cell, row: parseElecCell(cell).row, category: String(inst.category), name, lines, ownLines: [...lines], edited: false, moved: false, loads, various: isAudio(String(inst.category)), instId: inst.id });
    partAt.set(ELEC_KEY.part(inst.id), { x: inst.x, y: inst.y });
  }
  // What names a light group follows it: its key, else an older one — naming
  // any of its lights, or one of the lights stored for that key (`lightKeys`) —
  // once the light it was keyed by is gone or another comes first. An older key
  // goes to ONE group, its box's and its wire's edits together; a kit's box
  // takes its projector part's edits too.
  const groupKeys = new Set(groups.map((g) => g.key));
  const namesGroup = (k: string, g: LightGroup): boolean => {
    const head = ELEC_KEY.light(g.room.id, '');
    if (!k.startsWith(head) || groupKeys.has(k)) return false;
    const ids = new Set(g.ids);
    return ids.has(k.slice(head.length)) || !!sch.lightKeys?.[k]?.some((id) => ids.has(id));
  };
  const named = [...new Set([...Object.keys(sch.boxes ?? {}), ...Object.keys(sch.wires ?? {})].map(ELEC_KEY.lightOf).filter((k): k is string => !!k))];
  const claimedBy = new Map<string, LightGroup>();
  const older = new Map<string, { box?: string; wire?: string }>();
  for (const g of groups) {
    const needBox = !sch.boxes?.[g.key], needWire = !sch.wires?.[ELEC_KEY.lightWire(g.key)];
    const has = (k: string): { box?: string; wire?: string } => ({
      ...(needBox && sch.boxes?.[k] ? { box: k } : {}),
      ...(needWire && sch.wires?.[ELEC_KEY.lightWire(k)] ? { wire: ELEC_KEY.lightWire(k) } : {}),
    });
    const k = named.find((x) => !claimedBy.has(x) && namesGroup(x, g) && Object.keys(has(x)).length);
    const found = k ? has(k) : {};
    if (k) claimedBy.set(k, g);
    for (const id of g.projectors) if (kitOf.get(id) === g.key) Object.assign(found, { ...has(ELEC_KEY.part(id)), ...found });
    older.set(g.key, found);
  }
  for (const g of groups) {
    const editKey = older.get(g.key)?.box, projectors = g.projectors.filter((id) => kitOf.get(id) === g.key);
    boxes.push({ key: g.key, ...(editKey ? { editKey } : {}), ...(projectors.length ? { projectors } : {}), kind: 'light', cell: g.cell, autoCell: g.cell, row: parseElecCell(g.cell).row, category: 'lighting', name: g.name, lines: g.lines, ownLines: [...g.lines], edited: false, moved: false, loads: [{ name: g.name, power: g.watts }], various: true, roomId: g.room.id, lightIds: [...g.ids], ...(g.run ? { run: g.run } : {}), ...(g.runAt ? { runAt: g.runAt } : {}) });
  }
  const preps: Array<{ inst: ProductInstance; prepId: string }> = [];
  for (const inst of drawing.instances) {
    if (isRoom(inst)) continue;
    for (const p of inst.preparations ?? []) {
      if (p.type !== 'electrical') continue;
      // Its power as the plan's schedule shows it (`placedVariant`): a
      // multi-part product's on each of its parts' preparations.
      const v = placedVariant(drawing, inst);
      const lines = [i18n(p.label || '', p.labelI18n, lang)].filter((s) => s.trim());
      if (v?.power != null) lines.push(variantLine({ id: '', article: '', power: v.power, voltage: v.voltage }, lang));
      boxes.push({ key: ELEC_KEY.client(inst.id, p.id), kind: 'client', cell: 'client', autoCell: 'client', row: 'client', category: 'client', name: lines[0] ?? '', lines, ownLines: [...lines], edited: false, moved: false, loads: [], instId: inst.id, prepId: p.id });
      preps.push({ inst, prepId: p.id });
    }
  }
  for (const h of sch.handBoxes ?? []) {
    // Its own cell is where it was put; that one gone (its room, plant room or
    // hand column deleted), the cell it was moved to is, while that is there.
    const to = sch.boxes?.[ELEC_KEY.hand(h.id)]?.cell;
    const home = cellExists(h.cell) ? h.cell : to && cellExists(to) ? to : null;
    if (!home) continue;
    const text = h.text.trim() ? h.text : tr(ELEC_NEW_BOX);
    const lines = text.split('\n');
    // Its own power typed on its card is its load while it is an electrical load.
    const power = sch.boxes?.[ELEC_KEY.hand(h.id)]?.electricalLoad && h.power != null && h.power > 0 ? h.power : 0;
    const loads: ElecLoad[] = power ? [{ name: lines[0] ?? '', power }] : [];
    boxes.push({ key: ELEC_KEY.hand(h.id), kind: 'hand', cell: home, autoCell: home, row: parseElecCell(home).row, category: 'hand', name: lines[0] ?? '', lines, ownLines: [...lines], edited: false, moved: false, loads, handId: h.id });
  }

  // ---- The boxes' hand edits.
  const kept: ElecBox[] = [], hiddenBoxes: ElecBox[] = [];
  for (const b of boxes) {
    const e = sch.boxes?.[b.editKey ?? b.key];
    if (e?.hidden) { hiddenBoxes.push(b); continue; }
    const there = !!e?.cell && cellExists(e.cell);
    if (there) { b.cell = e!.cell!; b.row = parseElecCell(b.cell).row; }
    // Its hand order is the cell's it was written for: none while the cell it
    // was moved to is gone (a plant room, a hand column removed), nor once its
    // own cell is another than the one it was written for (`orderIn`: its part
    // moved on the plan). One stored before `orderIn` was: kept.
    if (e?.order != null && (e.cell ? there : e.orderIn == null || e.orderIn === b.autoCell)) { b.order = e.order; if (e.dragged) b.dragged = true; }
    if (e?.rotation != null) b.rotation = e.rotation;
    if (e?.text != null) { b.lines = e.text.split('\n'); b.ownLines = [...b.lines]; b.edited = true; }
    kept.push(b);
  }

  // ---- Columns: rooms with a box in them, in drawing order, and those added by hand.
  const editOf = new Map((sch.columns ?? []).map((c) => [c.id, c]));
  const used = new Set(kept.map((b) => parseElecCell(b.cell)).filter((c) => ELEC_COLUMN_ROWS.includes(c.row)).map((c) => c.key));
  // A room's column reads the room's plan label, numbered as the plan numbers it.
  let all: ElecColumn[] = [];
  rooms.forEach((r) => {
    if (!used.has(r.id)) return;
    all.push({ id: r.id, roomId: r.id, name: names.get(r.id) ?? r.name, color: '', hand: false });
  });
  for (const c of handCols) all.push({ id: c.id, name: tr(ELEC_NEW_COLUMN), color: '', hand: true });
  all.forEach((c, i) => {
    const e = editOf.get(c.id);
    c.color = e?.color ?? ELEC_COLUMN_COLORS[i % ELEC_COLUMN_COLORS.length];
    if (e?.name?.trim()) { c.name = e.name; c.entered = e.name; }
  });
  const order = sch.columnOrder ?? [];
  const rank = (c: ElecColumn): number => { const i = order.indexOf(c.id); return i < 0 ? order.length : i; };
  all = all.map((c, i) => ({ c, i })).sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i).map((x) => x.c);
  const columns = all.filter((c) => !editOf.get(c.id)?.hidden);
  const hiddenColumns = all.filter((c) => editOf.get(c.id)?.hidden);
  const shown = new Set(columns.map((c) => c.id));
  const outOfView = (b: ElecBox): boolean => {
    const { row, key } = parseElecCell(b.cell);
    return ELEC_COLUMN_ROWS.includes(row) && !shown.has(key);
  };
  // A hidden column, or a box hidden by hand, takes its part's client
  // provisions with it.
  const gone = new Set([...hiddenBoxes, ...kept.filter(outOfView)].map((b) => b.key));
  const inModel = kept.filter((b) => !outOfView(b) && !(b.kind === 'client' && b.instId && gone.has(partKey(b.instId))));
  inModel.sort((a, b) => compareCells(a.cell, b.cell, columns, plantRooms) || compareElecBoxes(a, b));
  const boxByKey = new Map(inModel.map((b) => [b.key, b]));
  const byCell = new Map<ElecCellId, ElecBox[]>();
  for (const b of inModel) (byCell.get(b.cell) ?? byCell.set(b.cell, []).get(b.cell)!).push(b);
  for (const list of byCell.values()) markMoved(list);

  // ---- Wires: the project's, then the automatic feeds, then those drawn by hand.
  const project: ElecWire[] = [];
  const prepNum = scheduleNumbers(preparationSchedule(drawing));
  const connNum = scheduleNumbers(connectionSchedule(drawing));
  const look = { thickness: 0, style: 'solid' as SchematicLineStyle, color: '' };
  // A wire of several parallel cables: its first line, then line k (`#k`), each
  // its number `.k`; its figures are its first line's (`firstOf`).
  const firstOf = new Map<ElecWire, ElecWire>();
  const countOf = (n: number | undefined): number => (n != null && Number.isFinite(n) ? Math.max(1, Math.round(n)) : 1);
  const parallel = (w: ElecWire, n: number): ElecWire[] => {
    if (n <= 1) return [w];
    const base = w.number, sub = (k: number): string => (base ? `${base}.${k}` : '');
    return Array.from({ length: n }, (_, i) => {
      if (!i) return Object.assign(w, { line: 1, lines: n, number: sub(1) });
      const k = i + 1, x: ElecWire = { ...w, key: ELEC_KEY.line(w.key, k), ...(w.editKey ? { editKey: ELEC_KEY.line(w.editKey, k) } : {}), line: k, lines: n, first: w.key, number: sub(k) };
      firstOf.set(x, w);
      return x;
    });
  };
  for (const { inst, prepId } of preps) {
    const key = ELEC_KEY.prep(inst.id, prepId);
    project.push(...parallel({ key, kind: 'prep', from: ELEC_KEY.client(inst.id, prepId), to: partKey(inst.id), number: prepNum.get(prepId) ?? '', installedBy: 'client', cable: '', look }, countOf(sch.wires?.[key]?.lines)));
  }
  for (const c of drawing.connections) {
    if (!isElecConn(c)) continue;
    const n = Math.max(1, Math.round(c.lines || 1));
    const lengthTyped = isComputedLength(c) ? undefined : c.length;
    const lengthAuto = opts.lengthOf?.(lengthTyped == null ? c : { ...c, length: undefined });
    const length = lengthTyped ?? lengthAuto;
    const cable = i18n(c.label || '', c.labelI18n, lang);
    for (let i = 0; i < n; i++) {
      const sub = n > 1 ? `.${i + 1}` : '';
      // Its first line's edits were once stored as `e:<c>:1` with two lines or more.
      const key = ELEC_KEY.conn(c.id, i + 1), old = `e:${c.id}:1`;
      const editKey = i === 0 && n > 1 && !sch.wires?.[key] && sch.wires?.[old] ? old : undefined;
      project.push({ key, ...(editKey ? { editKey } : {}), kind: 'project', from: partKey(c.from), to: partKey(c.to), number: (connNum.get(c.id) ?? '') + sub, installedBy: c.installedBy ?? 'stenal', length, lengthTyped, lengthAuto, cable, connectionId: c.id, look, ...(n > 1 ? { line: i + 1, lines: n, ...(i ? { first: ELEC_KEY.conn(c.id) } : {}) } : {}) });
    }
  }
  // A box named by an older key of it (a light group's, a kit projector's part's): that box.
  const boxOf = (k: string): string => {
    if (boxByKey.has(k)) return k;
    return kitByPartKey.get(k) ?? (claimedBy.get(k) ?? groups.find((g) => namesGroup(k, g)))?.key ?? k;
  };
  const hand: ElecWire[] = (sch.handWires ?? []).flatMap((h) => parallel({ key: ELEC_KEY.handWire(h.id), kind: 'hand', from: boxOf(h.from), to: boxOf(h.to), number: `S${h.n}`, installedBy: h.installedBy ?? 'stenal', length: h.length, lengthTyped: h.length, cable: h.cable ?? '', handId: h.id, look }, countOf(h.lines)));
  const wireThickness = sch.wireThickness != null && Number.isFinite(sch.wireThickness) && sch.wireThickness > 0 ? sch.wireThickness : ELEC_WIRE_THICKNESS;
  const wireStyle = sch.wireStyle ?? 'solid';
  const removedWires: ElecWire[] = [];
  const typedLength = (e: ElecWireEdit | undefined): number | undefined => (e?.length != null && Number.isFinite(e.length) && e.length >= 0 ? e.length : undefined);
  // A wire's hand edits on it: false when it is removed or does not join two
  // boxes. An automatic feed's far end is the one `feeds` gave it.
  const edited = (w: ElecWire): boolean => {
    const e = sch.wires?.[w.editKey ?? w.key];
    if (e?.removed) { removedWires.push(w); return false; }
    // Its figures: its first line's, for all its lines alike.
    const head = firstOf.get(w), f = head ? sch.wires?.[head.editKey ?? head.key] : e;
    const typed = w.kind === 'auto' || w.kind === 'prep';
    if (w.kind === 'prep' && f?.to && boxByKey.has(boxOf(f.to))) w.to = w.fed = boxOf(f.to);
    if (typed) {
      if (f?.installedBy) w.installedBy = f.installedBy;
      if (f?.cable != null) w.cable = f.cable;
      const len = typedLength(f);
      if (len != null) w.length = w.lengthTyped = len;
    }
    if (e?.jog != null && Number.isFinite(e.jog)) w.jog = clampElecJog(e.jog);
    if (e?.straight) w.straight = true;
    if (e?.thickness != null && Number.isFinite(e.thickness) && e.thickness > 0) w.thickness = e.thickness;
    if (e?.style) w.style = e.style;
    if (e?.color) w.color = e.color;
    w.look = { thickness: w.thickness ?? wireThickness, style: w.style ?? wireStyle, color: w.color ?? ELEC_INSTALLER_COLORS[w.installedBy] };
    return w.from !== w.to && boxByKey.has(w.from) && boxByKey.has(w.to);
  };
  const drawn = project.filter(edited), byHand = hand.filter(edited);
  const lightOf = new Map(groups.map((g) => [g.key, g]));
  // Where an automatic feed's edits are stored: its key, else an older one of its light group's.
  const wireKeyOf = (b: ElecBox): string => (lightOf.has(b.key) ? older.get(b.key)?.wire : undefined) ?? ELEC_KEY.lightWire(b.key);
  const feeds = elecFeedGraph({
    drawing, sch, inModel, boxByKey, wires: [...drawn, ...byHand], partAt, lightOf, boxOf, wireKeyOf, lengthOf: opts.lengthOf, typedLength,
  });
  // Numbered on after the project's last E, in the order the boxes stand, a
  // removed one leaving no gap.
  let lastE = 0;
  for (const m of [...prepNum.values(), ...connNum.values()]) { const r = /^E(\d+)$/.exec(m); if (r) lastE = Math.max(lastE, Number(r[1])); }
  const auto: ElecWire[] = [];
  for (const b of inModel) {
    const f = feeds.feed.get(b.key);
    if (!f) continue;
    const g = lightOf.get(b.key), key = ELEC_KEY.lightWire(b.key), editKey = g ? older.get(g.key)?.wire : undefined;
    const length = feeds.lengthOf(b, f.to);
    auto.push(...parallel({ key, ...(editKey ? { editKey } : {}), kind: 'auto', from: b.key, to: f.to.key, ...(f.hand ? { fed: f.to.key } : {}), number: '', installedBy: 'stenal', cable: '', ...(length != null ? { length, lengthAuto: length } : {}), look }, countOf(sch.wires?.[editKey ?? key]?.lines)));
  }
  const autoLive = auto.filter(edited);
  // One number for all of a wire's lines (`.k` each), a removed line leaving no gap.
  const autoNumber = new Map<string, string>();
  for (const w of autoLive) {
    const at = w.first ?? w.key;
    const n = autoNumber.get(at) ?? autoNumber.set(at, `E${++lastE}`).get(at)!;
    w.number = w.line ? `${n}.${w.line}` : n;
  }
  const live: ElecWire[] = [...drawn, ...autoLive, ...byHand];

  // ---- Decision 24: a client box listing the loads it feeds; any box its
  // electrical load (what it needs, the loads it feeds summed up to it).
  for (const b of inModel) {
    const e = sch.boxes?.[b.editKey ?? b.key];
    const listed = b.row === 'client' && !!e?.listLoads;
    if (!listed && !e?.electricalLoad) continue;
    const f = elecFeeds(b, inModel, live, boxByKey);
    if (listed) {
      b.feeds = f;
      if (!b.edited) {
        b.lines = [tr('Power supply, the sum of:'), ...f.loads.map((l) => loadLine(l, lang))];
        if (f.various > 0) b.lines.push(`${tr('Various loads')} ${elecPower(f.various, lang)}`);
        b.lines.push(`${tr('Total')} ${elecPower(f.total, lang)}`);
      }
    }
    if (e?.electricalLoad) {
      b.load = b.loads.reduce((sum, l) => sum + l.power, 0) + f.total;
      if (!listed || b.edited) b.lines = [...b.lines, `${tr('Total')} ${elecPower(b.load, lang)}`];
    }
  }

  const rows: ElecRow[] = ELEC_ROWS.map((id) => {
    const empty = !inModel.some((b) => b.row === id);
    return { id, name: elecRowName(id, tr), empty, hidden: empty && !!sch.hiddenRows?.includes(id) };
  });
  // The hand edits the layout reads, copied: an edit made on the drawing never
  // changes the model it was made from.
  const rowHeights = { ...(sch.rowHeights ?? {}) }, borders = (sch.borders ?? []).map((b) => ({ ...b }));
  return { lang, rows, columns, hiddenColumns, plantRooms, boxes: inModel, wires: live, boxByKey, fedBy: feeds.fedBy, hiddenBoxes, removedWires, rowHeights, borders, wireThickness, wireStyle };
}

/** The part a connection's end at box `b` is glued to: a part's box, its part;
 *  a fibre kit's (the projector part IS its box), its projector part — of
 *  several, the one nearest `near` on the plan (the connection's other end),
 *  none while two are as near or nothing is given. None for any other box. */
export function elecBoxPart(drawing: Pick<Drawing, 'instances'>, b: ElecBox | undefined, near?: Vec2): ProductInstance | undefined {
  const byId = (id: string | undefined): ProductInstance | undefined => (id ? drawing.instances.find((i) => i.id === id) : undefined);
  if (b?.kind === 'product') return byId(b.instId);
  const ps = (b?.projectors ?? []).map(byId).filter((i): i is ProductInstance => !!i);
  if (ps.length < 2) return ps[0];
  if (!near) return undefined;
  const d = ps.map((p) => Math.hypot(p.x - near.x, p.y - near.y)), min = Math.min(...d);
  const nearest = ps.filter((_, i) => d[i] - min < 1);
  return nearest.length === 1 ? nearest[0] : undefined;
}

/** Whether feeding box `box` from box `from` closes a loop: `from` is fed,
 *  through what feeds it (`ElecModel.fedBy`), by `box` itself. An automatic feed
 *  taken there is refused (elecEdits.ts, `loop`); one stored so is taken back to
 *  its rule's by the model. */
export function elecFeedLoops(model: Pick<ElecModel, 'fedBy'>, box: string, from: string): boolean {
  const seen = new Set<string>([from]), queue = [from];
  for (let i = 0; i < queue.length; i++) {
    if (queue[i] === box) return true;
    for (const n of model.fedBy.get(queue[i]) ?? []) if (!seen.has(n)) { seen.add(n); queue.push(n); }
  }
  return false;
}

interface FeedGraphIn {
  drawing: Drawing;
  sch: ElecSchematic;
  inModel: ElecBox[];
  boxByKey: Map<string, ElecBox>;
  /** The wires drawn: the project's, the preparations', those by hand (after their edits). */
  wires: ElecWire[];
  partAt: Map<string, Vec2>;
  lightOf: Map<string, LightGroup>;
  boxOf: (k: string) => string;
  /** Where a box's automatic feed's edits are stored. */
  wireKeyOf: (b: ElecBox) => string;
  lengthOf?: (c: Connection) => number | undefined;
  typedLength: (e: ElecWireEdit | undefined) => number | undefined;
}
interface Feed { to: ElecBox; hand: boolean; rule?: ElecBox }

/** Decision 26: who feeds whom. A box is fed by a wire drawn to a box in a row
 *  below it, or (no junction box itself) to a junction box in its own row.
 *  Junction boxes of one row wired to each other are ONE group, fed once: by a
 *  member a wire drawn feeds from below, else by the member with the shortest
 *  path to the client's supply (`path`), which keeps its automatic feed, the
 *  others fed through it. Every other part box and light group not fed takes an
 *  automatic feed: its wire edit's `to` (never into its own group), else the
 *  rule's (`feedCells`). Never a loop: a feed taken by hand that closes one goes
 *  back to the rule's. */
function elecFeedGraph(x: FeedGraphIn): { feed: Map<string, Feed>; fedBy: Map<string, string[]>; lengthOf: (b: ElecBox, to: ElecBox) => number | undefined } {
  const { drawing, sch, inModel, boxByKey, lightOf } = x;
  const slot = new Map(inModel.map((b, i) => [b.key, i]));
  const rowOf = (b: ElecBox): number => ELEC_ROWS.indexOf(b.row);
  const add = <T>(m: Map<string, T[]>, k: string, v: T): void => { (m.get(k) ?? m.set(k, []).get(k)!).push(v); };
  const below = new Map<string, Array<{ by: string; len: number }>>();
  const links = new Map<string, string[]>();
  for (const w of x.wires) {
    const a = boxByKey.get(w.from)!, b = boxByKey.get(w.to)!, ra = rowOf(a), rb = rowOf(b), len = w.length ?? 0;
    if (ra === rb && a.junction && b.junction) { add(links, a.key, b.key); add(links, b.key, a.key); continue; }
    if (rb > ra || (rb === ra && b.junction)) add(below, a.key, { by: b.key, len });
    if (ra > rb || (ra === rb && a.junction)) add(below, b.key, { by: a.key, len });
  }
  // The groups, each in slot order; a member fed through its group: by its head.
  const groupOf = new Map<string, string[]>();
  for (const b of inModel) {
    if (!links.has(b.key) || groupOf.has(b.key)) continue;
    const ms = [b.key];
    groupOf.set(b.key, ms);
    for (let i = 0; i < ms.length; i++) for (const n of links.get(ms[i]) ?? []) if (!groupOf.has(n) && boxByKey.has(n)) { groupOf.set(n, ms); ms.push(n); }
    ms.sort((p, q) => slot.get(p)! - slot.get(q)!);
  }
  const headOf = new Map<string, string>();
  const open: string[][] = [];
  for (const ms of new Set(groupOf.values())) {
    const fed = ms.find((k) => below.has(k));
    if (fed) { for (const k of ms) if (!below.has(k)) headOf.set(k, fed); } else open.push(ms);
  }
  // The automatic feeds, tentatively: every member of an open group its own.
  const lengths = new Map<string, number | undefined>();
  const lengthOf = (b: ElecBox, to: ElecBox): number | undefined => {
    const id = `${b.key}>${to.key}`;
    if (lengths.has(id)) return lengths.get(id);
    const len = feedRunLength(drawing, b, to, x);
    lengths.set(id, len);
    return len;
  };
  const wireEdit = (b: ElecBox): ElecWireEdit | undefined => sch.wires?.[x.wireKeyOf(b)];
  const lengthGiven = (b: ElecBox, to: ElecBox): number => x.typedLength(wireEdit(b)) ?? lengthOf(b, to) ?? 0;
  const junctions = inModel.filter((b) => b.junction && b.kind === 'product');
  const feed = new Map<string, Feed>();
  for (const b of inModel) {
    if ((b.kind !== 'product' && b.kind !== 'light') || below.has(b.key) || headOf.has(b.key)) continue;
    const g = lightOf.get(b.key);
    const at = g ? g.world ?? toWorld(g.room, g.at) : x.partAt.get(b.key);
    if (!at) continue;
    const dist = (j: ElecBox): number => { const p = x.partAt.get(j.key)!; return Math.hypot(p.x - at.x, p.y - at.y); };
    let rule: ElecBox | undefined;
    for (const under of feedCells(b.cell, !!b.junction)) {
      rule = junctions.filter((j) => j !== b && under(j.cell)).reduce<ElecBox | undefined>((best, j) => (!best || dist(j) < dist(best) ? j : best), undefined);
      if (rule) break;
    }
    const fedKey = wireEdit(b)?.to;
    const byHand = fedKey ? boxByKey.get(x.boxOf(fedKey)) : undefined;
    const to = byHand && byHand !== b && !groupOf.get(b.key)?.includes(byHand.key) ? byHand : undefined;
    if (to) feed.set(b.key, { to, hand: true, ...(rule ? { rule } : {}) });
    else if (rule) feed.set(b.key, { to: rule, hand: false, rule });
  }
  const feedersOf = (k: string): Array<{ by: string; len: number }> => {
    const out = [...(below.get(k) ?? [])];
    const f = feed.get(k), b = boxByKey.get(k);
    if (f && b) out.push({ by: f.to.key, len: lengthGiven(b, f.to) });
    const h = headOf.get(k);
    if (h) out.push({ by: h, len: 0 });
    return out;
  };
  const bySlot = (p: string, q: string): number => slot.get(p)! - slot.get(q)!;
  const findLoop = (): string[] | null => {
    const mark = new Map<string, 1 | 2>();
    for (const start of inModel) {
      if (mark.has(start.key)) continue;
      const stack: Array<{ k: string; next: string[]; i: number }> = [];
      const enter = (k: string): void => { mark.set(k, 1); stack.push({ k, next: feedersOf(k).map((f) => f.by), i: 0 }); };
      enter(start.key);
      while (stack.length) {
        const top = stack[stack.length - 1];
        if (top.i >= top.next.length) { mark.set(top.k, 2); stack.pop(); continue; }
        const n = top.next[top.i++];
        if (mark.get(n) === 1) return stack.slice(stack.findIndex((s) => s.k === n)).map((s) => s.k);
        if (!mark.has(n) && boxByKey.has(n)) enter(n);
      }
    }
    return null;
  };
  const breakLoops = (): void => {
    for (let n = 0; n <= 2 * inModel.length; n++) {
      const loop = findLoop();
      if (!loop) return;
      const k = loop.filter((m) => feed.get(m)?.hand).sort(bySlot)[0] ?? loop.filter((m) => feed.has(m)).sort(bySlot)[0];
      if (!k) return;
      const f = feed.get(k)!;
      if (f.hand && f.rule) feed.set(k, { to: f.rule, hand: false, rule: f.rule }); else feed.delete(k);
    }
  };
  /** Each box's path down to the client's supply: the feed wires' lengths summed,
   *  by its shortest way down (0 where nothing feeds it). */
  const paths = (): Map<string, number> => {
    const d = new Map(inModel.map((b) => [b.key, feedersOf(b.key).length ? Infinity : 0]));
    for (let n = 0; n <= inModel.length; n++) {
      let changed = false;
      for (const b of inModel) for (const f of feedersOf(b.key)) {
        const v = f.len + (d.get(f.by) ?? Infinity);
        if (v < d.get(b.key)!) { d.set(b.key, v); changed = true; }
      }
      if (!changed) break;
    }
    return d;
  };
  breakLoops();
  // An open group's head, row by row up from the bottom (what is below decided first).
  for (const row of [...ELEC_ROWS].reverse()) {
    const here = open.filter((ms) => boxByKey.get(ms[0])!.row === row);
    if (!here.length) continue;
    const d = paths();
    for (const ms of here) {
      const path = (k: string): number => { const f = feed.get(k); return f ? lengthGiven(boxByKey.get(k)!, f.to) + (d.get(f.to.key) ?? Infinity) : Infinity; };
      const head = ms.reduce((best, k) => (path(k) < path(best) ? k : best), ms[0]);
      for (const k of ms) if (k !== head) { feed.delete(k); headOf.set(k, head); }
    }
  }
  breakLoops();
  const fedBy = new Map(inModel.map((b) => [b.key, [...new Set(feedersOf(b.key).map((f) => f.by))]]));
  return { feed, fedBy, lengthOf };
}

/** An automatic feed's length as decision 10 has it, through the caller's
 *  lengthOf: from the part, or the light (a strip or a channel half way up its
 *  room, the rest at its ceiling) — a fibre kit from its projector part — to the
 *  box it is fed from. */
function feedRunLength(drawing: Drawing, b: ElecBox, to: ElecBox, x: Pick<FeedGraphIn, 'lightOf' | 'lengthOf'>): number | undefined {
  const toInst = to.instId ?? x.lightOf.get(to.key)?.projectors[0];
  if (!toInst || !x.lengthOf) return undefined;
  const key = ELEC_KEY.lightWire(b.key), g = x.lightOf.get(b.key);
  let run: Connection | undefined;
  if (g?.projectors.length === 1) run = { id: key, from: g.projectors[0], to: toInst, lines: 1 };
  else if (g?.projectors.length) {
    const z = g.projectors.reduce((s, id) => s + (objectMidHeight(drawing, id) ?? 0), 0) / g.projectors.length;
    run = { id: key, from: '', to: toInst, lines: 1, fromEnd: { p: g.world ?? toWorld(g.room, g.at), z } };
  } else if (g) {
    const top = roomBase(g.room) + externalHeight(g.room), kind = g.lights[0].kind;
    const z = kind === 'strip' || kind === 'channel' ? top - externalHeight(g.room) / 2 : top;
    run = { id: key, from: '', to: toInst, lines: 1, fromEnd: { p: toWorld(g.room, g.at), z } };
  } else if (b.instId) run = { id: key, from: b.instId, to: toInst, lines: 1 };
  return run ? x.lengthOf(run) : undefined;
}

/** Where a box in `cell` looks for its junction box, tier by tier: its own cell
 *  and the cell below together, then each cell further down; a junction box
 *  (`junction`) only the cells below its own. */
function feedCells(cell: ElecCellId, junction: boolean): Array<(c: ElecCellId) => boolean> {
  const below = cellsBelow(cell);
  if (junction) return below;
  const own = (c: ElecCellId): boolean => c === cell;
  if (!below.length) return [own];
  const next = below[0];
  return [(c) => own(c) || next(c), ...below.slice(1)];
}

/** The cells below one, cell by cell down: its column's cells below it, then
 *  the technical rooms (any of them). */
function cellsBelow(cell: ElecCellId): Array<(c: ElecCellId) => boolean> {
  const { row, key } = parseElecCell(cell);
  const i = ELEC_COLUMN_ROWS.indexOf(row);
  if (i < 0) return [];
  return [
    ...ELEC_COLUMN_ROWS.slice(i + 1).map((r) => (c: ElecCellId): boolean => c === elecCellId(r, key)),
    (c: ElecCellId): boolean => parseElecCell(c).row === 'technical',
  ];
}

/** Cells in grid order: by row, then column / plant room order. */
function compareCells(a: ElecCellId, b: ElecCellId, columns: ElecColumn[], plants: ElecPlantRoom[]): number {
  const ca = parseElecCell(a), cb = parseElecCell(b);
  const r = ELEC_ROWS.indexOf(ca.row) - ELEC_ROWS.indexOf(cb.row);
  if (r) return r;
  const idx = (c: { row: ElecRowId; key: string }): number => (c.row === 'technical' ? plants.findIndex((p) => p.id === c.key) : columns.findIndex((x) => x.id === c.key));
  return idx(ca) - idx(cb);
}

/** What a box feeds: power goes from it up the schematic, along every wire, to
 *  the boxes in its own row or above (never back down a row, nor into a client
 *  box), and on from each of them; a part with a client supply of its own — a
 *  wire to another client box — is fed by that one only, unless the client box
 *  walked from is wired to it directly. Lights and audio summed as one
 *  "Various loads". */
export function elecFeeds(from: ElecBox, boxes: ElecBox[], wires: ElecWire[], byKey: Map<string, ElecBox>): NonNullable<ElecBox['feeds']> {
  const adj = new Map<string, string[]>();
  for (const w of wires) {
    (adj.get(w.from) ?? adj.set(w.from, []).get(w.from)!).push(w.to);
    (adj.get(w.to) ?? adj.set(w.to, []).get(w.to)!).push(w.from);
  }
  const rowOf = (k: string): number => ELEC_ROWS.indexOf(byKey.get(k)?.row ?? 'client');
  const supplied = (k: string): boolean => (adj.get(k) ?? []).some((n) => n !== from.key && byKey.get(n)?.row === 'client');
  const seen = new Set<string>([from.key]);
  const queue = [from.key];
  for (let i = 0; i < queue.length; i++) {
    const cur = queue[i];
    for (const n of adj.get(cur) ?? []) {
      if (seen.has(n) || !byKey.has(n) || byKey.get(n)!.row === 'client' || rowOf(n) > rowOf(cur)) continue;
      if (!(cur === from.key && from.row === 'client') && supplied(n)) continue;
      seen.add(n); queue.push(n);
    }
  }
  const loads: ElecLoad[] = [];
  let various = 0;
  for (const b of boxes) {
    if (!seen.has(b.key) || b === from) continue;
    for (const l of b.loads) { if (b.various) various += l.power; else loads.push(l); }
  }
  return { loads, various, total: loads.reduce((s, l) => s + l.power, 0) + various };
}
